import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  KEY_ENTER_COMMAND,
  type EditorState,
  type LexicalEditor,
  type TextNode,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createMentionNode, MentionNode } from './MentionNode';
import { MentionList } from './MentionList';
import { segmentMentions, type ComposerMentionItem, type ComposerMentions } from './mention';
import {
  editable as editableCls,
  editorWrap,
  paragraph as paragraphCls,
  placeholder as placeholderCls,
} from './mention.css';

export interface MentionEditorProps {
  /** Current text (mentions serialised inline as `${trigger}Label`). */
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter / Cmd·Ctrl+Enter when sending is allowed. */
  onSend: (value: string) => void;
  /**
   * Explicit send override (e.g. over-length, in-flight). When omitted, sending
   * is gated on the editor's LIVE emptiness — so a just-pasted/typed value sends
   * even before the parent re-renders the controlled `value`.
   */
  sendDisabled?: boolean;
  placeholder: string;
  /** Accessible name for the editable. */
  ariaLabel: string;
  /** Classes for the editable surface (shared textArea + prominence classes). */
  editableClassName: string;
  /** Mention typeahead config, or undefined to disable it. */
  mentions?: ComposerMentions;
  /** Populated with the send action, so the out-of-editor send button reads live content. */
  sendRef: React.MutableRefObject<() => void>;
}

/** Builds an editor's node list from a plain string (mentions → atomic chips). */
function $buildNodes(value: string, isMention: (word: string) => boolean) {
  const nodes = [];
  for (const seg of segmentMentions(value, isMention)) {
    if (seg.mention) {
      nodes.push($createMentionNode(seg.text));
      continue;
    }
    const lines = seg.text.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) nodes.push($createLineBreakNode());
      if (line) nodes.push($createTextNode(line));
    });
  }
  return nodes;
}

/** Reads the editor's plain-text value (mention chips serialise to their text). */
function readValue(editor: LexicalEditor): string {
  return editor.getEditorState().read(() => $getRoot().getTextContent());
}

/** Reflects an external `value` into the editor, without clobbering local edits. */
function ValueSyncPlugin({
  value,
  isMention,
}: {
  value: string;
  isMention: (word: string) => boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const isMentionRef = useRef(isMention);
  isMentionRef.current = isMention;

  useEffect(() => {
    if (readValue(editor) === value) return; // our own edit round-tripped — skip
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const para = $createParagraphNode();
      para.append(...$buildNodes(value, isMentionRef.current));
      root.append(para);
      para.selectEnd();
    });
  }, [editor, value]);
  return null;
}

/** Enter / Cmd·Ctrl+Enter send; Shift+Enter inserts a newline. */
function SendPlugin({
  onSend,
  sendDisabled,
  sendRef,
}: {
  onSend: (value: string) => void;
  sendDisabled?: boolean;
  /** Populated with the send action so the (out-of-editor) send button can call it. */
  sendRef: React.MutableRefObject<() => void>;
}) {
  const [editor] = useLexicalComposerContext();
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  const disabledRef = useRef(sendDisabled);
  disabledRef.current = sendDisabled;

  // Both the send button and Enter route through this ONE live read of the
  // editor, so they can never diverge on a stale value — and, with no explicit
  // override, emptiness is judged from the LIVE editor content (not the parent's
  // lagging controlled `value`), so a just-entered value sends immediately.
  const doSend = useCallback(() => {
    const text = readValue(editor);
    const disabled = disabledRef.current ?? text.trim().length === 0;
    if (!disabled) onSendRef.current(text);
  }, [editor]);

  useEffect(() => {
    sendRef.current = doSend;
  }, [sendRef, doSend]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          // Shift+Enter → newline (let the default plain-text handler run).
          if (event?.shiftKey) return false;
          // Plain Enter or Cmd/Ctrl+Enter → send (never insert a newline).
          event?.preventDefault();
          doSend();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, doSend],
  );
  return null;
}

/** One agent row in the typeahead. */
class MentionOption extends MenuOption {
  item: ComposerMentionItem;
  constructor(item: ComposerMentionItem) {
    super(item.id);
    this.item = item;
  }
}

/** `@`-typeahead: shows the agent list, inserts an atomic chip on select. */
function MentionsPlugin({ mentions }: { mentions: ComposerMentions }) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const trigger = mentions.trigger ?? '@';
  const triggerFn = useBasicTypeaheadTriggerMatch(trigger, { minLength: 0 });

  const items = useMemo(() => (query != null ? mentions.getItems(query) : []), [query, mentions]);
  const options = useMemo(() => items.map((it) => new MentionOption(it)), [items]);

  const onSelectOption = useCallback(
    (option: MentionOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const chip = $createMentionNode(`${trigger}${option.item.label}`);
        if (nodeToReplace) nodeToReplace.replace(chip);
        const space = $createTextNode(' ');
        chip.insertAfter(space);
        space.select();
        closeMenu();
      });
      mentions.onSelect?.(option.item);
    },
    [editor, trigger, mentions],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      options={options}
      // Beat the send handler (COMMAND_PRIORITY_LOW): while the menu is open,
      // Enter/Tab must select the highlighted agent, not submit the message.
      commandPriority={COMMAND_PRIORITY_NORMAL}
      menuRenderFn={(anchorRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorRef.current && options.length > 0
          ? createPortal(
              <MentionList
                items={items}
                activeIndex={selectedIndex ?? 0}
                query={query ?? ''}
                listLabel={mentions.listLabel ?? 'Suggestions'}
                onHover={setHighlightedIndex}
                onSelect={(i) => {
                  setHighlightedIndex(i);
                  selectOptionAndCleanUp(options[i]!);
                }}
              />,
              anchorRef.current,
            )
          : null
      }
    />
  );
}

/**
 * MentionEditor — the Composer's text field, a Lexical plain-text editor. Same
 * behaviour as the old textarea (Enter sends, Shift+Enter newlines, Cmd/Ctrl+
 * Enter sends, empty = no send, auto-grows then scrolls), plus a `mentions`
 * typeahead that inserts real, atomic, styled `@Label` chips. Controlled via
 * `value` / `onChange`; the value is plain text with mentions serialised inline.
 */
export function MentionEditor({
  value,
  onChange,
  onSend,
  sendDisabled,
  placeholder,
  ariaLabel,
  editableClassName,
  mentions,
  sendRef,
}: MentionEditorProps) {
  const isMention = useCallback(
    (word: string) =>
      mentions
        ? mentions.getItems(word).some((i) => i.label.toLowerCase() === word.toLowerCase())
        : false,
    [mentions],
  );

  const initialConfig = useMemo(
    () => ({
      namespace: 'composer',
      nodes: [MentionNode],
      theme: { paragraph: paragraphCls },
      onError: (error: Error) => {
        throw error;
      },
      editorState: () => {
        const root = $getRoot();
        if (root.getFirstChild() !== null) return;
        const para = $createParagraphNode();
        para.append(...$buildNodes(value, isMention));
        root.append(para);
      },
    }),
    // Config is created once; `value` seeds only the initial state, and
    // ValueSyncPlugin owns updates thereafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const lastEmitted = useRef(value);
  const handleChange = useCallback(
    (editorState: EditorState) => {
      const text = editorState.read(() => $getRoot().getTextContent());
      if (text === lastEmitted.current) return;
      lastEmitted.current = text;
      onChange(text);
    },
    [onChange],
  );

  return (
    <div className={editorWrap}>
      <LexicalComposer initialConfig={initialConfig}>
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className={`${editableClassName} ${editableCls}`}
              ariaLabel={ariaLabel}
              aria-placeholder={placeholder}
              placeholder={<div className={placeholderCls}>{placeholder}</div>}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        <ValueSyncPlugin value={value} isMention={isMention} />
        <SendPlugin onSend={onSend} sendDisabled={sendDisabled} sendRef={sendRef} />
        {mentions && <MentionsPlugin mentions={mentions} />}
      </LexicalComposer>
    </div>
  );
}
MentionEditor.displayName = 'MentionEditor';
